import { makeObservable, observable } from 'mobx'

import { LocationType, PositionSource } from '~/client/graph'
import FieldIds from '~/client/src/shared/enums/DeliveryFieldIds'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import { IWorkflowRestrictions } from '~/client/src/shared/types/ILocationAttributeDto'

import IHierarchyParent from '../../types/IHierarchyParent'
import IMsDateInterval from '../../types/IMsDateInterval'
import { areArraysEqual, copyArray } from '../../utils/util'
import IAccessibleLevelsAttribute from '../IAccessibleLevelsAttribute'
import LocationAttributeBase from './LocationAttributeBase'

interface IEffectivePosition {
  source: PositionSource
  latitude?: number | null
  longitude?: number | null
  lastUpdatedAt?: string | null
}

interface IOffloadingEquipmentDto extends IAccessibleLevelsAttribute {
  templateId?: string | null
  data?: Record<string, any> | null
  effectivePosition?: IEffectivePosition | null
  stxResourceId?: string | null
}

export default class OffloadingEquipment extends LocationAttributeBase<OffloadingEquipment> {
  public constructor(
    id: string,
    name: string,
    color: string,
    iconName: MapViewLocationIcon,
    projectId: string,
    code?: string,
    operatingIntervals?: IMsDateInterval[],
    parent?: IHierarchyParent,
    permittedCompanies?: string[],
    isDeleted?: boolean,
    assignedSitemaps?: string[],
    assignedGlobes?: string[],
    maxBookingDuration?: number,
    maxOverlappingDeliveries?: number,
    workflowRestrictions?: IWorkflowRestrictions,
    public templateId?: string | null,
    public data?: Record<string, any> | null,
  ) {
    super(
      id,
      name,
      color,
      iconName,
      projectId,
      code,
      operatingIntervals,
      parent,
      permittedCompanies,
      isDeleted,
      assignedSitemaps,
      assignedGlobes,
      maxBookingDuration,
      maxOverlappingDeliveries,
      workflowRestrictions,
    )

    makeObservable(this)
  }

  public static fromDto(dto: IOffloadingEquipmentDto) {
    const item = LocationAttributeBase.factory(
      OffloadingEquipment,
      dto,
      MapViewLocationIcon.Equipment,
    )

    item.accessibleLevels = dto.accessibleLevels || []
    item.templateId = dto.templateId || null
    item.data = dto.data || null
    item.effectivePosition = dto.effectivePosition || null
    item.stxResourceId = dto.stxResourceId || null

    return item
  }

  @observable public accessibleLevels: string[] = []
  @observable public effectivePosition: IEffectivePosition | null = null

  public type = LocationType.OffloadingEquipment
  protected relatedFieldId = FieldIds.OFFLOADING_EQUIPMENT
  protected model = OffloadingEquipment

  public isEqual(dto: OffloadingEquipment) {
    return (
      super.isEqual(dto) &&
      areArraysEqual(this.accessibleLevels, dto.accessibleLevels) &&
      this.templateId === dto.templateId &&
      JSON.stringify(this.data) === JSON.stringify(dto.data) &&
      this.stxResourceId === dto.stxResourceId
    )
  }

  public copy(): OffloadingEquipment {
    const item = super.copy()
    item.accessibleLevels = copyArray(this.accessibleLevels || [])
    item.effectivePosition = this.effectivePosition
      ? { ...this.effectivePosition }
      : null
    return item
  }

  public getDto(): IOffloadingEquipmentDto {
    const dto = super.getDto() as IOffloadingEquipmentDto
    dto.accessibleLevels = this.accessibleLevels || []
    // templateId and data come from the base now, which every location needs.
    return dto
  }
}
